import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CustomMonthPicker } from '../components/ui/custom-month-picker';
import { CustomDateTimePicker } from '../components/ui/custom-datetime-picker';
import { toast } from 'sonner';
import { Users, Plus, IndianRupee, Clock, User, Phone, Wallet, Edit2, Trash2, FileText, Calendar, ArrowDownCircle, ArrowUpCircle, UserPlus, Banknote } from 'lucide-react';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import ErrorDialog from '../components/ErrorDialog';
import { getTodayString, getCurrentMonthString, getCurrentDateTimeString, dateToMonthString, dateToString } from '../utils/dateUtils';
import { 
  SalaryCard, 
  EditAdvanceDialog, 
  EditOvertimeDialog,
  ADVANCE_REASONS, 
  PAYMENT_METHODS, 
  preventWheelChange,
  AddEmployeeDialog,
  EditEmployeeDialog,
  EmployeeList,
  EmptyEmployeeDetails,
  EmployeeHeader
} from '../components/employees';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

export default function EmployeeManagement() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [salary, setSalary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonthString());
  const [employeeSalaryData, setEmployeeSalaryData] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    monthly_salary: '',
    start_date: getTodayString(),
    end_date: ''
  });
  const [phoneError, setPhoneError] = useState('');
  const [editPhoneError, setEditPhoneError] = useState('');
  const [editData, setEditData] = useState({
    name: '',
    contact: '',
    monthly_salary: '',
    start_date: '',
    end_date: '',
    salary_increment: 'No',
    new_salary: '',
    new_salary_start_date: '',
    pending_increment: null
  });
  const [advanceData, setAdvanceData] = useState({ 
    amount: '', 
    date: getTodayString(),
    reason: '',
    other_reason: '',
    payment_method: 'UPI',
    notes: '' 
  });
  const [overtimeData, setOvertimeData] = useState({ 
    start_datetime: '',
    end_datetime: '',
    notes: '' 
  });
  const [deductionData, setDeductionData] = useState({ 
    amount: '', 
    month: getCurrentMonthString(),
    notes: '',
    category: '' 
  });
  const [calculatedHours, setCalculatedHours] = useState(null);
  const [otRangeError, setOtRangeError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentMonthHistory, setCurrentMonthHistory] = useState(null);
  const [currentMonthLoading, setCurrentMonthLoading] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState(null);
  const [editingOvertime, setEditingOvertime] = useState(null);
  const [showEditAdvanceDialog, setShowEditAdvanceDialog] = useState(false);
  const [showEditOvertimeDialog, setShowEditOvertimeDialog] = useState(false);
  
  // Delete confirmation states
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: '', title: '', message: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // OT month confirmation dialog
  const [otConfirm, setOtConfirm] = useState({ open: false, monthName: '', month: '' });
  
  // Pay Salary confirmation dialog
  const [salaryPreview, setSalaryPreview] = useState({ open: false, loading: false, data: null });
  
  // Error dialog state
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '', fieldName: '', fieldId: '' });
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = currentUser.role === 'approver' || currentUser.role === 'super_approver';

  // Get previous month string (correctly) - For Feb 2026, should return 2026-01
  const getPreviousMonth = () => {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth(); // 0-indexed: Jan=0, Feb=1, etc.
    
    // If current month is January (0), go to December of previous year
    if (month === 0) {
      return `${year - 1}-12`;
    }
    // Otherwise, subtract 1 from current month
    return `${year}-${String(month).padStart(2, '0')}`;
  };
  
  // Get current month string - For Feb 2026, should return 2026-02
  const getCurrentMonthStr = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  
  const [historyMonth, setHistoryMonth] = useState(getCurrentMonthString());
  const [currentMonthStr] = useState(getCurrentMonthStr());

  useEffect(() => {
    fetchEmployees();
    checkMonthlyReset();
  }, []);

  // Update deduction month default when employee changes (for terminated employees)
  useEffect(() => {
    if (selectedEmployee) {
      const endDate = selectedEmployee.end_date || selectedEmployee.termination_date;
      if (endDate) {
        // Set the deduction month to the employee's last working month
        const lastMonth = endDate.slice(0, 7); // YYYY-MM format
        setDeductionData(prev => ({ ...prev, month: lastMonth }));
      } else {
        // Reset to current month for active employees
        setDeductionData(prev => ({ ...prev, month: getCurrentMonthString() }));
      }
    }
  }, [selectedEmployee]);

  // Check and perform monthly reset on page load
  const checkMonthlyReset = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/employees/monthly-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reset_performed) {
        toast.info(`Monthly reset completed: All employee advance and overtime values have been reset to 0 for ${response.data.reset_month}`);
      }
    } catch (error) {
      // Silently fail - this is a background operation
      console.log('Monthly reset check:', error.response?.data?.detail || 'Failed');
    }
  };

  useEffect(() => {
    if (overtimeData.start_datetime && overtimeData.end_datetime) {
      const startDate = new Date(overtimeData.start_datetime);
      const endDate = new Date(overtimeData.end_datetime);
      
      if (endDate > startDate) {
        const diffMs = endDate - startDate;
        const hours = diffMs / (1000 * 60 * 60);
        setCalculatedHours(hours.toFixed(2));
        setOtRangeError(null);
      } else {
        setCalculatedHours(null);
        setOtRangeError('End date and time should not be less than or equal to start date and time');
      }
    } else {
      setCalculatedHours(null);
      setOtRangeError(null);
    }
  }, [overtimeData.start_datetime, overtimeData.end_datetime]);

  const fetchEmployees = async (isRetry = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000
      });
      setEmployees(response.data);
      
      // Fetch salary data for all employees (current and previous month)
      fetchAllEmployeeSalaries(response.data, token);
    } catch (error) {
      console.error('Employee fetch error:', error);
      if (!isRetry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        setTimeout(() => fetchEmployees(true), 2000);
        return;
      }
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check your connection');
      } else if (error.response?.status >= 500) {
        toast.error('Server temporarily unavailable');
      } else {
        toast.error('Failed to load employees');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSalary = async (employeeId, month) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${employeeId}/salary?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalary(response.data);
    } catch (error) {
      toast.error('Failed to calculate salary');
    }
  };

  // Fetch salary data for all employees (current and previous month)
  const fetchAllEmployeeSalaries = async (employeeList, token) => {
    const now = new Date();
    const currMonth = dateToMonthString(now);
    const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonth = dateToMonthString(prevDate);
    
    const salaryData = {};
    
    for (const emp of employeeList) {
      try {
        const [currRes, prevRes] = await Promise.all([
          axios.get(`${API}/employees/${emp.employee_id}/salary?month=${currMonth}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null })),
          axios.get(`${API}/employees/${emp.employee_id}/salary?month=${prevMonth}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        ]);
        
        salaryData[emp.employee_id] = {
          curr: currRes.data,
          prev: prevRes.data
        };
      } catch (error) {
        console.error(`Failed to fetch salary for ${emp.name}:`, error);
      }
    }
    
    setEmployeeSalaryData(salaryData);
  };

  const fetchHistory = async (employeeId) => {
    if (!employeeId) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${employeeId}/history?month=${historyMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchCurrentMonthHistory = async (employeeId) => {
    if (!employeeId) return;
    setCurrentMonthLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${employeeId}/history?month=${currentMonthStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentMonthHistory(response.data);
    } catch (error) {
      toast.error('Failed to load current month data');
    } finally {
      setCurrentMonthLoading(false);
    }
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    if (!/^\d+$/.test(phone)) return 'Phone number must contain only digits';
    if (phone.length !== 10) return 'Phone number must be exactly 10 digits';
    return '';
  };

  const handlePhoneChange = (value, isEdit = false) => {
    // Remove non-digits and limit to 10 characters
    const cleanValue = value.replace(/\D/g, '').slice(0, 10);
    
    if (isEdit) {
      setEditData({ ...editData, contact: cleanValue });
      setEditPhoneError(validatePhone(cleanValue));
    } else {
      setFormData({ ...formData, contact: cleanValue });
      setPhoneError(validatePhone(cleanValue));
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    
    // Validate phone before submit
    const phoneErr = validatePhone(formData.contact);
    if (phoneErr) {
      setPhoneError(phoneErr);
      toast.error(phoneErr);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee added successfully!');
      setShowAddDialog(false);
      setFormData({ name: '', contact: '', monthly_salary: '', start_date: getTodayString(), end_date: '' });
      setPhoneError('');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    
    // Validate phone before submit
    const phoneErr = validatePhone(editData.contact);
    if (phoneErr) {
      setEditPhoneError(phoneErr);
      toast.error(phoneErr);
      return;
    }
    
    // Validate salary increment fields if increment is Yes
    if (editData.salary_increment === 'Yes') {
      if (!editData.new_salary) {
        toast.error('Please enter the new salary');
        return;
      }
      if (parseFloat(editData.new_salary) <= parseFloat(editData.monthly_salary)) {
        toast.error('New salary must be greater than current salary');
        return;
      }
      if (!editData.new_salary_start_date) {
        toast.error('Please select the start date for new salary');
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data to send
      const dataToSend = {
        name: editData.name,
        contact: editData.contact,
        monthly_salary: editData.monthly_salary,
        start_date: editData.start_date,
        end_date: editData.end_date
      };
      
      // Add salary increment data if applicable
      if (editData.salary_increment === 'Yes' && editData.new_salary && editData.new_salary_start_date) {
        dataToSend.pending_increment = {
          new_salary: parseFloat(editData.new_salary),
          start_date: editData.new_salary_start_date
        };
      }
      
      await axios.put(`${API}/employees/${selectedEmployee.employee_id}`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee updated successfully!');
      setShowEditDialog(false);
      fetchEmployees();
      // Update selected employee
      setSelectedEmployee({ ...selectedEmployee, ...editData });
      fetchSalary(selectedEmployee.employee_id, currentMonth);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    setDeleteConfirm({
      open: true,
      type: 'employee',
      id: employeeId,
      title: 'Delete Employee',
      message: 'Are you sure you want to delete this employee? This will also delete all their advance and overtime records.'
    });
  };
  
  const confirmDelete = async () => {
    setDeleteLoading(true);
    const { type, id } = deleteConfirm;
    
    try {
      const token = localStorage.getItem('token');
      
      if (type === 'employee') {
        await axios.delete(`${API}/employees/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee deleted successfully!');
        if (selectedEmployee?.employee_id === id) {
          setSelectedEmployee(null);
          setSalary(null);
        }
        fetchEmployees();
      } else if (type === 'advance') {
        await axios.delete(`${API}/employees/advance/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Advance payment deleted');
        fetchSalary(selectedEmployee.employee_id, currentMonth);
        fetchCurrentMonthHistory(selectedEmployee.employee_id);
        fetchHistory(selectedEmployee.employee_id);
      } else if (type === 'overtime') {
        await axios.delete(`${API}/employees/overtime/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Overtime record deleted');
        fetchSalary(selectedEmployee.employee_id, currentMonth);
        fetchCurrentMonthHistory(selectedEmployee.employee_id);
        fetchHistory(selectedEmployee.employee_id);
      } else if (type === 'deduction') {
        await axios.delete(`${API}/employees/deduction/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Deduction record deleted');
        fetchSalary(selectedEmployee.employee_id, currentMonth);
        fetchCurrentMonthHistory(selectedEmployee.employee_id);
        fetchHistory(selectedEmployee.employee_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm({ open: false, type: '', id: '', title: '', message: '' });
    }
  };

  const openEditDialog = (emp) => {
    setEditData({
      name: emp.name,
      contact: emp.contact,
      monthly_salary: emp.monthly_salary.toString(),
      start_date: emp.start_date || '',
      end_date: emp.end_date || '',
      salary_increment: 'No',
      new_salary: '',
      new_salary_start_date: '',
      pending_increment: emp.pending_increment || null
    });
    setShowEditDialog(true);
  };

  const handleAddAdvance = async (e) => {
    e.preventDefault();
    if (!advanceData.reason) {
      setErrorDialog({
        open: true,
        message: 'Please select a reason for the advance payment',
        fieldName: 'Reason',
        fieldId: 'advance-reason'
      });
      return;
    }
    if (advanceData.reason === 'other' && !advanceData.other_reason) {
      setErrorDialog({
        open: true,
        message: 'Please enter the reason for this advance',
        fieldName: 'Other Reason',
        fieldId: 'advance-other-reason'
      });
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees/${selectedEmployee.employee_id}/advance`, {
        employee_id: selectedEmployee.employee_id,
        amount: parseFloat(advanceData.amount),
        date: advanceData.date,
        reason: advanceData.reason,
        other_reason: advanceData.reason === 'other' ? advanceData.other_reason : null,
        payment_method: advanceData.payment_method,
        month: currentMonth,
        notes: advanceData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advance payment recorded');
      setAdvanceData({ 
        amount: '', 
        date: getTodayString(),
        reason: '',
        other_reason: '',
        payment_method: 'UPI',
        notes: '' 
      });
      fetchSalary(selectedEmployee.employee_id, currentMonth);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add advance');
    }
  };

  const handleAddOvertime = async (e) => {
    e.preventDefault();
    if (!overtimeData.start_datetime || !overtimeData.end_datetime) {
      setErrorDialog({
        open: true,
        message: 'Please enter start and end date/time for overtime',
        fieldName: 'Start/End DateTime',
        fieldId: 'overtime-start-datetime'
      });
      return;
    }
    
    const startDate = new Date(overtimeData.start_datetime);
    const endDate = new Date(overtimeData.end_datetime);
    
    if (endDate <= startDate) {
      setErrorDialog({
        open: true,
        message: 'End date and time should not be less than or equal to start date and time',
        fieldName: 'End Date & Time',
        fieldId: 'overtime-end-datetime'
      });
      return;
    }
    
    // Get months from start and end dates
    const startMonth = overtimeData.start_datetime.slice(0, 7); // YYYY-MM format
    const endMonth = overtimeData.end_datetime.slice(0, 7);
    const currentMonthValue = getCurrentMonthString();
    
    // Check if OT falls in a previous month (use start date for confirmation)
    if (startMonth < currentMonthValue) {
      const monthName = startDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      setOtConfirm({
        open: true,
        monthName: monthName,
        month: startMonth
      });
      return;
    }
    
    // Proceed with submission
    await submitOvertime();
  };
  
  // Submit overtime with automatic month splitting
  const submitOvertime = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees/${selectedEmployee.employee_id}/overtime`, {
        employee_id: selectedEmployee.employee_id,
        start_datetime: overtimeData.start_datetime,
        end_datetime: overtimeData.end_datetime,
        notes: overtimeData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Overtime recorded: ${calculatedHours} hours`);
      setOvertimeData({ 
        start_datetime: '',
        end_datetime: '',
        notes: '' 
      });
      setCalculatedHours(null);
      fetchSalary(selectedEmployee.employee_id, currentMonth);
      fetchCurrentMonthHistory(selectedEmployee.employee_id);
      fetchHistory(selectedEmployee.employee_id);
      // Refresh employee salary data
      const token2 = localStorage.getItem('token');
      fetchAllEmployeeSalaries(employees, token2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add overtime');
    }
  };
  
  // Confirm OT for previous month
  const handleConfirmOt = async () => {
    setOtConfirm({ ...otConfirm, open: false });
    await submitOvertime();
  };

  // Edit advance handler
  const handleEditAdvance = async (e) => {
    e.preventDefault();
    if (!editingAdvance) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/employees/advance/${editingAdvance.advance_id}`, {
        employee_id: selectedEmployee.employee_id,
        amount: parseFloat(editingAdvance.amount),
        date: editingAdvance.date,
        reason: editingAdvance.reason,
        other_reason: editingAdvance.reason === 'other' ? editingAdvance.other_reason : null,
        payment_method: editingAdvance.payment_method,
        month: editingAdvance.month,
        notes: editingAdvance.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advance payment updated');
      setShowEditAdvanceDialog(false);
      setEditingAdvance(null);
      fetchSalary(selectedEmployee.employee_id, currentMonth);
      fetchCurrentMonthHistory(selectedEmployee.employee_id);
      fetchHistory(selectedEmployee.employee_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update advance');
    }
  };

  // Delete advance handler
  const handleDeleteAdvance = async (advanceId) => {
    setDeleteConfirm({
      open: true,
      type: 'advance',
      id: advanceId,
      title: 'Delete Advance',
      message: 'Are you sure you want to delete this advance? This will also delete the linked expense.'
    });
  };

  // Helper to prepare overtime record for editing (convert legacy format to new format)
  const prepareOvertimeForEdit = (record) => {
    // If record already has start_datetime and end_datetime, use them directly
    if (record.start_datetime && record.end_datetime) {
      return record;
    }
    
    // Convert legacy format (date + start_time + end_time) to new format
    const startDatetime = `${record.date}T${record.start_time}`;
    let endDatetime = `${record.date}T${record.end_time}`;
    
    // Handle overnight shift - if end time is less than start time, add a day
    if (record.end_time < record.start_time) {
      const endDate = new Date(record.date);
      endDate.setDate(endDate.getDate() + 1);
      endDatetime = `${dateToString(endDate)}T${record.end_time}`;
    }
    
    return {
      ...record,
      start_datetime: startDatetime,
      end_datetime: endDatetime
    };
  };

  // Edit overtime handler
  const handleEditOvertimeClick = (record) => {
    setEditingOvertime({
      overtime_id: record.overtime_id,
      start_datetime: record.start_datetime,
      end_datetime: record.end_datetime,
      notes: record.notes || ''
    });
    setShowEditOvertimeDialog(true);
  };

  const handleEditOvertime = async (e) => {
    e.preventDefault();
    if (!editingOvertime) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/employees/overtime/${editingOvertime.overtime_id}`, {
        employee_id: selectedEmployee.employee_id,
        start_datetime: editingOvertime.start_datetime,
        end_datetime: editingOvertime.end_datetime,
        notes: editingOvertime.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Overtime record updated');
      setShowEditOvertimeDialog(false);
      setEditingOvertime(null);
      fetchSalary(selectedEmployee.employee_id, currentMonth);
      fetchCurrentMonthHistory(selectedEmployee.employee_id);
      fetchHistory(selectedEmployee.employee_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update overtime');
    }
  };

  // Delete overtime handler
  const handleDeleteOvertime = async (overtimeId) => {
    setDeleteConfirm({
      open: true,
      type: 'overtime',
      id: overtimeId,
      title: 'Delete Overtime',
      message: 'Are you sure you want to delete this overtime record?'
    });
  };

  // Add deduction handler
  const handleAddDeduction = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    if (!deductionData.amount || parseFloat(deductionData.amount) <= 0) {
      toast.error('Please enter a valid deduction amount');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employees/${selectedEmployee.employee_id}/deduction`, {
        employee_id: selectedEmployee.employee_id,
        amount: parseFloat(deductionData.amount),
        month: deductionData.month,
        notes: deductionData.notes,
        category: deductionData.category || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deduction added successfully');
      setDeductionData({ amount: '', month: getCurrentMonthString(), notes: '', category: '' });
      fetchSalary(selectedEmployee.employee_id, currentMonth);
      fetchCurrentMonthHistory(selectedEmployee.employee_id);
      fetchHistory(selectedEmployee.employee_id);
      // Refresh employee salary data
      const token2 = localStorage.getItem('token');
      fetchAllEmployeeSalaries(employees, token2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deduction');
    }
  };

  // Delete deduction handler
  const handleDeleteDeduction = async (deductionId) => {
    setDeleteConfirm({
      open: true,
      type: 'deduction',
      id: deductionId,
      title: 'Delete Deduction',
      message: 'Are you sure you want to delete this deduction record?'
    });
  };

  // Edit deduction handler
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [showEditDeductionDialog, setShowEditDeductionDialog] = useState(false);

  const handleEditDeduction = async (e) => {
    e.preventDefault();
    if (!editingDeduction) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/employees/deduction/${editingDeduction.deduction_id}`, {
        employee_id: selectedEmployee.employee_id,
        amount: parseFloat(editingDeduction.amount),
        month: editingDeduction.month,
        notes: editingDeduction.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deduction updated');
      setShowEditDeductionDialog(false);
      setEditingDeduction(null);
      fetchSalary(selectedEmployee.employee_id, currentMonth);
      fetchCurrentMonthHistory(selectedEmployee.employee_id);
      fetchHistory(selectedEmployee.employee_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deduction');
    }
  };

  // Pay Salary handler - first shows preview, then creates salary expenses
  const [payingSalary, setPayingSalary] = useState(false);
  const [selectedSalaryMonth, setSelectedSalaryMonth] = useState('');  // YYYY-MM format
  
  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };
  
  // Fetch salary preview before paying
  const handleShowSalaryPreview = async (month) => {
    const targetMonth = month || selectedSalaryMonth;
    try {
      setSalaryPreview({ open: true, loading: true, data: null });
      const token = localStorage.getItem('token');
      const url = targetMonth 
        ? `${API}/employees/salary-preview?month=${targetMonth}` 
        : `${API}/employees/salary-preview`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalaryPreview({ open: true, loading: false, data: response.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load salary preview');
      setSalaryPreview({ open: false, loading: false, data: null });
    }
  };
  
  const handlePaySalary = async () => {
    if (payingSalary) return;
    
    try {
      setPayingSalary(true);
      setSalaryPreview({ ...salaryPreview, open: false });
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/employees/pay-salary`, 
        { month: selectedSalaryMonth || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { created, skipped, details } = response.data;
      
      if (created > 0) {
        toast.success(`Salary paid for ${created} employee(s)`);
      }
      
      // Show specific skip reasons
      if (details && details.length > 0) {
        const alreadyExists = details.filter(d => d.status === 'skipped' && d.reason === 'already exists');
        const joinedAfter = details.filter(d => d.status === 'skipped' && d.reason?.includes('joined after'));
        const otherSkipped = details.filter(d => d.status === 'skipped' && d.reason !== 'already exists' && !d.reason?.includes('joined after'));
        
        if (alreadyExists.length > 0) {
          toast.info(`${alreadyExists.length} employee(s) already have salary for this month`);
        }
        if (joinedAfter.length > 0) {
          const names = joinedAfter.map(d => d.employee).join(', ');
          toast.info(`Skipped ${joinedAfter.length}: ${names} (joined this month)`);
        }
        if (otherSkipped.length > 0) {
          otherSkipped.forEach(d => {
            toast.warning(`${d.employee}: ${d.reason}`);
          });
        }
      }
      
      if (created === 0 && skipped === 0) {
        toast.info('No employees found to process');
      }
      
      // Refresh data
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create salary expenses');
    } finally {
      setPayingSalary(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-slate-500">
            <div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
            <span className="font-medium">Loading employees...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-slate-500 text-sm font-bold tracking-[0.15em] uppercase mb-1">Payroll</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Employee Management
            </h1>
            <p className="text-slate-500 mt-1">Manage employees, payroll, advances and overtime</p>
          </div>
          {isApprover && (
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  // Default to previous month
                  const now = new Date();
                  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                  const defaultMonth = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
                  setSelectedSalaryMonth(defaultMonth);
                  handleShowSalaryPreview(defaultMonth);
                }}
                disabled={payingSalary}
                className="h-11 w-11 p-0 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-500/30"
                title="Pay Salary"
              >
                {payingSalary ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Banknote className="w-5 h-5" />
                )}
              </Button>
              <AddEmployeeDialog
                open={showAddDialog}
                onOpenChange={setShowAddDialog}
                formData={formData}
                setFormData={setFormData}
                phoneError={phoneError}
                handlePhoneChange={handlePhoneChange}
                onSubmit={handleAddEmployee}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employee List */}
          <EmployeeList
            employees={employees}
            selectedEmployee={selectedEmployee}
            onSelectEmployee={(emp) => {
              setSelectedEmployee(emp);
              fetchSalary(emp.employee_id, currentMonth);
            }}
            onEditEmployee={(emp) => { setSelectedEmployee(emp); openEditDialog(emp); }}
            onDeleteEmployee={handleDeleteEmployee}
            isApprover={isApprover}
            employeeSalaryData={employeeSalaryData}
          />

          {/* Employee Details */}
          {selectedEmployee ? (
            <Card className="lg:col-span-2 p-6 border-0 rounded-2xl shadow-lg bg-white">
              {/* Employee Header */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/30 flex-shrink-0">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-2xl font-bold text-slate-900 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {selectedEmployee.name}
                    </h2>
                    <div className="flex items-center gap-2 text-slate-500 text-sm mt-1">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{selectedEmployee.contact}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <Label className="text-xs text-slate-500 font-semibold block mb-1">Month</Label>
                    <CustomMonthPicker
                      value={currentMonth}
                      onChange={(val) => {
                        if (val) {
                          setCurrentMonth(val);
                          fetchSalary(selectedEmployee.employee_id, val);
                        }
                      }}
                      min={selectedEmployee?.start_date?.slice(0, 7)}
                      max={getCurrentMonthString()}
                      className="w-40 h-9 text-sm"
                    />
                  </div>
                  {isApprover && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(selectedEmployee)}
                      className="rounded-lg mt-5"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Salary Summary */}
              {salary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <SalaryCard
                    label="Base Salary"
                    value={salary.base_salary}
                    color="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-900"
                  />
                  <SalaryCard
                    label="Overtime"
                    value={salary.overtime_payment}
                    subtext={`${salary.overtime_hours}hrs × ₹${salary.hourly_rate}`}
                    color="bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-900"
                  />
                  <SalaryCard
                    label="Remaining Advance"
                    value={salary.available_advance}
                    color="bg-gradient-to-br from-amber-50 to-amber-100 text-amber-900"
                  />
                  <SalaryCard
                    label="Deduction"
                    value={salary.deduction || 0}
                    color="bg-gradient-to-br from-red-50 to-red-100 text-red-900"
                  />
                  <SalaryCard
                    label="Net Payment"
                    value={salary.net_payment}
                    color="bg-gradient-to-br from-slate-800 to-slate-900 text-white"
                  />
                </div>
              )}

              {/* Add Advance / Overtime / Deduction / Records Tabs */}
              {isApprover && (
              <Tabs defaultValue="overtime" className="w-full" onValueChange={(value) => {
                if (value === 'records' && selectedEmployee?.employee_id) {
                  fetchHistory(selectedEmployee.employee_id);
                }
              }}>
                <TabsList className="grid w-full grid-cols-3 h-12 bg-slate-100 rounded-xl p-1">
                  <TabsTrigger value="overtime" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold text-xs sm:text-sm">
                    <Clock className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Add </span>OT
                  </TabsTrigger>
                  <TabsTrigger value="deduction" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold text-xs sm:text-sm">
                    <ArrowDownCircle className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Add </span>Ded
                  </TabsTrigger>
                  <TabsTrigger value="records" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold text-xs sm:text-sm">
                    <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                    Records
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="overtime" className="mt-4">
                  <form onSubmit={handleAddOvertime} className="space-y-4" style={{ outline: 'none' }}>
                    {/* In Time */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">In Time *</Label>
                      <CustomDateTimePicker
                        value={overtimeData.start_datetime}
                        onChange={(val) => setOvertimeData({ ...overtimeData, start_datetime: val })}
                        placeholder="Select start date & time"
                        label="In Time"
                        min={selectedEmployee?.start_date || undefined}
                        max={(() => {
                          const today = getTodayString();
                          const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
                          if (endDate && endDate < today) return endDate;
                          return today;
                        })()}
                      />
                    </div>
                    
                    {/* Out Time */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Out Time *</Label>
                      <CustomDateTimePicker
                        value={overtimeData.end_datetime}
                        onChange={(val) => setOvertimeData({ ...overtimeData, end_datetime: val })}
                        placeholder="Select end date & time"
                        label="Out Time"
                        min={selectedEmployee?.start_date || undefined}
                        max={(() => {
                          const today = getTodayString();
                          const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
                          if (endDate && endDate < today) return endDate;
                          return today;
                        })()}
                      />
                    </div>
                    
                    {otRangeError && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <p className="text-red-600 font-medium">
                          ⚠️ {otRangeError}
                        </p>
                      </div>
                    )}
                    
                    {calculatedHours && !otRangeError && (
                      <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
                        <p className="text-emerald-800 font-bold">
                          Calculated: <span className="text-2xl">{calculatedHours} hrs</span>
                          <span className="text-sm font-normal ml-2">
                            ({Math.floor(calculatedHours)} hr {Math.round((calculatedHours % 1) * 60)} min)
                          </span>
                        </p>
                        {overtimeData.start_datetime && overtimeData.end_datetime && 
                         overtimeData.start_datetime.slice(0, 7) !== overtimeData.end_datetime.slice(0, 7) && (
                          <p className="text-amber-600 text-sm mt-2">
                            ⚠️ OT spans across months - will be split automatically
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="space-y-1.5">
                      <Label className="text-sm font-semibold">Notes (Optional)</Label>
                      <Input
                        value={overtimeData.notes}
                        onChange={(e) => setOvertimeData({ ...overtimeData, notes: e.target.value })}
                        placeholder="Any additional notes"
                        className="h-10 rounded-xl text-xs"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={!!otRangeError}
                      className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Record Overtime
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Deduction Tab */}
                <TabsContent value="deduction" className="mt-4">
                  <form onSubmit={handleAddDeduction} className="space-y-4" style={{ outline: 'none' }}>
                    {/* Month Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Month *</Label>
                      <CustomMonthPicker
                        value={deductionData.month}
                        onChange={(val) => setDeductionData({ ...deductionData, month: val })}
                        min={selectedEmployee?.start_date ? selectedEmployee.start_date.slice(0, 7) : undefined}
                        max={(() => {
                          const currentMonth = getCurrentMonthString();
                          const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
                          if (endDate) {
                            const endMonth = endDate.slice(0, 7);
                            return endMonth < currentMonth ? endMonth : currentMonth;
                          }
                          return currentMonth;
                        })()}
                      />
                      {(selectedEmployee?.end_date || selectedEmployee?.termination_date) && (
                        <p className="text-xs text-amber-600">
                          Note: Employee terminated on {(() => {
                            const dateStr = selectedEmployee?.end_date || selectedEmployee?.termination_date;
                            const [year, month, day] = dateStr.split('-').map(Number);
                            return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                          })()}
                        </p>
                      )}
                    </div>
                    
                    {/* Deduction Amount */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Amount *</Label>
                      <div className="relative">
                        <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="number"
                          value={deductionData.amount}
                          onChange={(e) => setDeductionData({ ...deductionData, amount: e.target.value })}
                          onWheel={preventWheelChange}
                          placeholder="Enter deduction amount"
                          className="h-10 pl-9 rounded-xl text-xs"
                          inputMode="numeric"
                        />
                      </div>
                    </div>
                    
                    {/* Notes */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-semibold">Category (Optional)</Label>
                      <select
                        value={deductionData.category}
                        onChange={(e) => setDeductionData({ ...deductionData, category: e.target.value })}
                        className="w-full h-10 rounded-xl text-xs border border-slate-200 px-3 bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid="deduction-category-select"
                      >
                        <option value="">Select category</option>
                        <option value="Ration">Ration</option>
                        <option value="Recharge">Recharge</option>
                        <option value="Advance Deducted">Advance Deducted</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Notes */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-semibold">Notes (Optional)</Label>
                      <Input
                        value={deductionData.notes}
                        onChange={(e) => setDeductionData({ ...deductionData, notes: e.target.value })}
                        placeholder="Reason for deduction"
                        className="h-10 rounded-xl text-xs"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg shadow-red-500/30"
                    >
                      <ArrowDownCircle className="w-4 h-4 mr-2" />
                      Add Deduction
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Records Tab - Combined with month filter */}
                <TabsContent value="records" className="mt-4">
                  {/* Month Filter */}
                  <div className="flex items-center gap-3 mb-4">
                    <CustomMonthPicker
                      value={historyMonth}
                      onChange={(newMonth) => {
                        const minMonth = selectedEmployee?.start_date?.slice(0, 7);
                        if (minMonth && newMonth < minMonth) {
                          toast.error(`Cannot select month before joining date (${new Date(minMonth + '-15').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })})`);
                          return;
                        }
                        setHistoryMonth(newMonth);
                        if (selectedEmployee?.employee_id) {
                          (async () => {
                            setHistoryLoading(true);
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`${API}/employees/${selectedEmployee.employee_id}/history?month=${newMonth}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setHistoryData(response.data);
                            } catch (error) {
                              toast.error('Failed to load records');
                            } finally {
                              setHistoryLoading(false);
                            }
                          })();
                        }
                      }}
                      min={selectedEmployee?.start_date?.slice(0, 7)}
                      max={getCurrentMonthString()}
                    />
                  </div>
                  
                  {historyLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
                    </div>
                  ) : historyData ? (
                    <div className="space-y-4">
                      {/* Month Header */}
                      <div className="bg-gradient-to-r from-slate-100 to-slate-200 rounded-xl p-4">
                        <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-1">
                          {(() => {
                            const [year, month] = historyData.month.split('-');
                            return new Date(parseInt(year), parseInt(month) - 1, 15).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                          })()}
                        </h4>
                        <p className="text-xs text-slate-500">Monthly salary breakdown</p>
                      </div>
                      
                      {/* Salary Summary Cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <SalaryCard label="Base Salary" value={historyData.base_salary} color="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-900" />
                        <SalaryCard label="Overtime" value={historyData.overtime_payment} subtext={`${historyData.total_overtime_hours}hrs`} color="bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-900" />
                        <SalaryCard label="Remaining Advance" value={historyData.available_advance} color="bg-gradient-to-br from-amber-50 to-amber-100 text-amber-900" />
                        <SalaryCard label="Deduction" value={historyData.total_deduction || 0} color="bg-gradient-to-br from-red-50 to-red-100 text-red-900" />
                      </div>
                      
                      {/* Net Payment Card */}
                      <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-xl p-4">
                        <p className="text-xs font-medium opacity-75 uppercase tracking-wider">Net Payment</p>
                        <p className="text-2xl font-bold">₹{(historyData.net_payment || 0).toLocaleString('en-IN')}</p>
                      </div>
                      
                      {/* Advance Details */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
                          <h5 className="font-semibold text-amber-900 flex items-center gap-2">
                            <ArrowDownCircle className="w-4 h-4" />
                            Advance Details ({historyData.advance_records?.length || 0})
                            <span className="ml-auto font-bold text-amber-600">₹{(historyData.total_advance || 0).toLocaleString('en-IN')}</span>
                          </h5>
                        </div>
                        {historyData.advance_records && historyData.advance_records.length > 0 ? (
                          <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                            {historyData.advance_records.map((record, idx) => (
                              <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 group">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-4 h-4 text-amber-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-900">
                                      {new Date(record.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {record.reason_display || record.reason} • {record.payment_method || 'UPI'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-amber-600">₹{record.amount.toLocaleString('en-IN')}</span>
                                  {isApprover && (
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => { setEditingAdvance(record); setShowEditAdvanceDialog(true); }} className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-600" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                                      <button onClick={() => handleDeleteAdvance(record.advance_id)} className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-6 text-center text-slate-400">
                            <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No advances recorded</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Overtime Details */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100">
                          <h5 className="font-semibold text-emerald-900 flex items-center gap-2">
                            <ArrowUpCircle className="w-4 h-4" />
                            Overtime Details ({historyData.overtime_records?.length || 0})
                            <span className="ml-auto font-bold text-emerald-600">{historyData.total_overtime_hours?.toFixed(1) || 0} hrs</span>
                          </h5>
                        </div>
                        {historyData.overtime_records && historyData.overtime_records.length > 0 ? (
                          <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                            {historyData.overtime_records.map((record, idx) => (
                              <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 group">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-4 h-4 text-emerald-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-900">
                                      {record.start_datetime
                                        ? new Date(record.start_datetime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                        : new Date(record.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                      }
                                    </p>
                                    <p className="text-xs text-slate-500">
                                      {record.start_datetime && record.end_datetime ? (
                                        <>
                                          {new Date(record.start_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                          {' - '}
                                          {new Date(record.end_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                        </>
                                      ) : (
                                        `${record.start_time} - ${record.end_time}`
                                      )}
                                      {record.notes && ` • ${record.notes}`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-emerald-600">{record.hours} hrs</span>
                                  {isApprover && (
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => handleEditOvertimeClick(record)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                                      <button onClick={() => handleDeleteOvertime(record.overtime_id)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-6 text-center text-slate-400">
                            <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No overtime recorded</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Deduction Details */}
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-red-50 px-4 py-3 border-b border-red-100">
                          <h5 className="font-semibold text-red-900 flex items-center gap-2">
                            <ArrowDownCircle className="w-4 h-4" />
                            Deduction Details ({historyData.deduction_records?.length || 0})
                            <span className="ml-auto font-bold text-red-600">₹{(historyData.total_deduction || 0).toLocaleString('en-IN')}</span>
                          </h5>
                        </div>
                        {historyData.deduction_records && historyData.deduction_records.length > 0 ? (
                          <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto">
                            {historyData.deduction_records.map((record, idx) => (
                              <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 group">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                    <ArrowDownCircle className="w-4 h-4 text-red-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-900">
                                      {(() => {
                                        const [year, month] = record.month.split('-');
                                        return new Date(parseInt(year), parseInt(month) - 1, 15).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
                                      })()}
                                    </p>
                                    {record.notes && <p className="text-xs text-slate-500">{record.notes}</p>}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-red-600">₹{record.amount.toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-6 text-center text-slate-400">
                            <ArrowDownCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No deductions recorded</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a month to view records</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
              )}
            </Card>
          ) : (
            <EmptyEmployeeDetails />
          )}
        </div>
      </div>

      {/* Edit Employee Dialog */}
      <EditEmployeeDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        editData={editData}
        setEditData={setEditData}
        editPhoneError={editPhoneError}
        handlePhoneChange={handlePhoneChange}
        onSubmit={handleEditEmployee}
      />

      {/* Edit Advance Dialog */}
      <EditAdvanceDialog
        open={showEditAdvanceDialog}
        onClose={setShowEditAdvanceDialog}
        advanceData={editingAdvance}
        setAdvanceData={setEditingAdvance}
        onSubmit={handleEditAdvance}
        selectedEmployee={selectedEmployee}
      />

      {/* Edit Overtime Dialog */}
      <EditOvertimeDialog
        open={showEditOvertimeDialog}
        onClose={setShowEditOvertimeDialog}
        overtimeData={editingOvertime}
        setOvertimeData={setEditingOvertime}
        onSubmit={handleEditOvertime}
        selectedEmployee={selectedEmployee}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: '', id: '', title: '', message: '' })}
        onConfirm={confirmDelete}
        title={deleteConfirm.title}
        message={deleteConfirm.message}
        loading={deleteLoading}
      />
      
      {/* OT Month Confirmation Dialog */}
      <Dialog open={otConfirm.open} onOpenChange={(open) => setOtConfirm({ ...otConfirm, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Confirm Overtime Month
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              The selected date is from a previous month. Is this overtime for <span className="font-bold text-amber-600">{otConfirm.monthName}</span>?
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setOtConfirm({ ...otConfirm, open: false })}
              className="flex-1 h-11 rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmOt}
              className="flex-1 h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl"
            >
              Yes, Add OT
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Error Dialog */}
      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ ...errorDialog, open: false })}
        message={errorDialog.message}
        fieldName={errorDialog.fieldName}
        fieldId={errorDialog.fieldId}
      />
      
      {/* Pay Salary Preview Dialog */}
      <Dialog open={salaryPreview.open} onOpenChange={(open) => setSalaryPreview({ ...salaryPreview, open })}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <span className="truncate">Pay Salary - {salaryPreview.data?.month || 'Select Month'}</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Month Selector */}
          <div className="mt-2">
            <Label className="text-xs text-slate-500 mb-1 block">Select Month to Pay</Label>
            <Select 
              value={selectedSalaryMonth} 
              onValueChange={(val) => {
                setSelectedSalaryMonth(val);
                handleShowSalaryPreview(val);
              }}
              data-testid="salary-month-select"
            >
              <SelectTrigger className="h-11 rounded-xl">
                <SelectValue placeholder="Select month..." />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {salaryPreview.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : salaryPreview.data ? (
            <div className="space-y-4 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-emerald-600">{salaryPreview.data.total_eligible}</p>
                  <p className="text-[10px] sm:text-xs text-emerald-700">Eligible</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-xl sm:text-2xl font-bold text-amber-600">{salaryPreview.data.total_skipped}</p>
                  <p className="text-[10px] sm:text-xs text-amber-700">Skipped</p>
                </div>
                <div className="bg-slate-100 rounded-xl p-2 sm:p-3 text-center">
                  <p className="text-sm sm:text-lg font-bold text-slate-800 truncate">₹{salaryPreview.data.total_amount?.toLocaleString('en-IN')}</p>
                  <p className="text-[10px] sm:text-xs text-slate-600">Total</p>
                </div>
              </div>
              
              {/* Eligible Employees */}
              {salaryPreview.data.eligible?.length > 0 && (
                <div className="border border-emerald-200 rounded-xl overflow-hidden">
                  <div className="bg-emerald-50 px-3 sm:px-4 py-2 border-b border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 text-xs sm:text-sm">Eligible ({salaryPreview.data.eligible.length})</h4>
                  </div>
                  <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                    {salaryPreview.data.eligible.map((emp, idx) => (
                      <div key={idx} className="px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm">
                        <span className="font-medium text-slate-800 truncate">{emp.name}</span>
                        <span className="text-emerald-600 font-bold whitespace-nowrap">₹{emp.net_payment?.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Skipped Employees */}
              {salaryPreview.data.skipped?.length > 0 && (
                <div className="border border-amber-200 rounded-xl overflow-hidden">
                  <div className="bg-amber-50 px-3 sm:px-4 py-2 border-b border-amber-200">
                    <h4 className="font-semibold text-amber-800 text-xs sm:text-sm">Skipped ({salaryPreview.data.skipped.length})</h4>
                  </div>
                  <div className="max-h-32 overflow-y-auto divide-y divide-slate-100">
                    {salaryPreview.data.skipped.map((emp, idx) => (
                      <div key={idx} className="px-3 sm:px-4 py-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0.5 sm:gap-2 text-xs sm:text-sm">
                        <span className="font-medium text-slate-600 truncate">{emp.name}</span>
                        <span className="text-amber-600 text-[10px] sm:text-xs whitespace-nowrap">{emp.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex gap-2 sm:gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setSalaryPreview({ open: false, loading: false, data: null })}
                  className="flex-1 h-10 sm:h-11 rounded-xl text-sm"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handlePaySalary}
                  disabled={salaryPreview.data.total_eligible === 0 || payingSalary}
                  className="flex-1 h-10 sm:h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl disabled:opacity-50 text-sm"
                >
                  {payingSalary ? 'Processing...' : `Pay ₹${salaryPreview.data.total_amount?.toLocaleString('en-IN')}`}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Deduction Dialog */}
      <Dialog open={showEditDeductionDialog} onOpenChange={setShowEditDeductionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <ArrowDownCircle className="w-5 h-5 text-red-500" />
              Edit Deduction
            </DialogTitle>
          </DialogHeader>
          {editingDeduction && (
            <form onSubmit={handleEditDeduction} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Month</Label>
                <CustomMonthPicker
                  value={editingDeduction.month}
                  onChange={(val) => setEditingDeduction({ ...editingDeduction, month: val })}
                  min={selectedEmployee?.start_date ? selectedEmployee.start_date.slice(0, 7) : undefined}
                  max={(() => {
                    const currentMonth = getCurrentMonthString();
                    const endDate = selectedEmployee?.end_date || selectedEmployee?.termination_date;
                    if (endDate) {
                      const endMonth = endDate.slice(0, 7);
                      return endMonth < currentMonth ? endMonth : currentMonth;
                    }
                    return currentMonth;
                  })()}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Amount</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={editingDeduction.amount}
                    onChange={(e) => setEditingDeduction({ ...editingDeduction, amount: e.target.value })}
                    onWheel={preventWheelChange}
                    className="h-10 pl-9 rounded-xl"
                    inputMode="numeric"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Notes</Label>
                <Input
                  value={editingDeduction.notes || ''}
                  onChange={(e) => setEditingDeduction({ ...editingDeduction, notes: e.target.value })}
                  placeholder="Reason for deduction"
                  className="h-10 rounded-xl"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowEditDeductionDialog(false); setEditingDeduction(null); }}
                  className="flex-1 h-11 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl"
                >
                  Update Deduction
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
